/**
 * Merchant Onboarding Page — multi-step merchant application flow.
 *
 * Steps:
 * 1. Business details (trading name, MCC, website, processing volume, billing
 *    descriptor, customer service contact, delivery timeframe, business
 *    description, registered address)
 * 2. Principal/owner details (linked to KYC, multiple principals, residential
 *    address, ID document upload)
 * 3. Processing details (currencies, expected volume, refund policy, max
 *    transaction, previous processor, chargeback rates, recurring/intl flags)
 * 4. Compliance declarations (expanded regulatory set)
 * 5. Document upload (named document slots with required/conditional/recommended)
 * 6. Summary/Review (full application review before submission)
 *
 * @module pages/MerchantOnboardingPage
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { applicant } from '@noxverify/api-contracts';
import { StepIndicator, type Step } from '../components/StepIndicator';
import { MccSelector } from '../components/MccSelector';
import { FileUpload } from '../components/FileUpload';
import { getApiClient } from '../lib/api';

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

function getMerchantSteps(t: TFunction): Step[] {
  return [
    { key: 'business', label: t('stepIndicator.steps.business') },
    { key: 'principal', label: t('merchantOnboarding.steps.businessProfile.title') },
    { key: 'processing', label: t('merchantOnboarding.steps.paymentProfile.title') },
    { key: 'compliance', label: t('merchantOnboarding.steps.compliance.title') },
    { key: 'documents', label: t('stepIndicator.steps.documents') },
    { key: 'review', label: t('stepIndicator.steps.review') },
  ];
}

// ---------------------------------------------------------------------------
// Step metadata (icons, titles, subtitles)
// ---------------------------------------------------------------------------

interface StepMeta {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}

const STEP_META: StepMeta[] = [
  {
    title: 'Business Details',
    subtitle:
      'Payment networks and regulators require accurate business information to underwrite your merchant account.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
  },
  {
    title: 'Principal / Owner Details',
    subtitle:
      'Regulations require identification of all individuals with significant ownership or control of the business.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    title: 'Processing Details',
    subtitle:
      'Transaction processing details help underwriters assess risk and configure appropriate limits.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
      </svg>
    ),
  },
  {
    title: 'Compliance Declarations',
    subtitle:
      'These declarations are required by payment regulations and your merchant agreement.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    title: 'Supporting Documents',
    subtitle:
      'Upload the required documents to support your merchant application.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    title: 'Review Your Application',
    subtitle:
      'Please review all information below before submitting. Click "Edit" on any section to make changes.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
  },
];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

function getDeliveryTimeframes(
  t: TFunction,
): { value: string; label: string }[] {
  return [
    { value: '', label: t('merchantOnboarding.deliveryTimeframes.selectPlaceholder') },
    { value: 'immediate', label: t('merchantOnboarding.deliveryTimeframes.immediate') },
    { value: '1-3_days', label: t('merchantOnboarding.deliveryTimeframes.1_3_days') },
    { value: '3-7_days', label: t('merchantOnboarding.deliveryTimeframes.3_7_days') },
    { value: '7-14_days', label: t('merchantOnboarding.deliveryTimeframes.7_14_days') },
    { value: '14-30_days', label: t('merchantOnboarding.deliveryTimeframes.14_30_days') },
    { value: 'over_30_days', label: t('merchantOnboarding.deliveryTimeframes.over_30_days') },
  ];
}

const CURRENCY_OPTIONS = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK',
  'SGD', 'HKD', 'NZD', 'BRL', 'MXN', 'INR', 'ZAR', 'PLN', 'CZK', 'HUF',
];

const BILLING_DESCRIPTOR_MAX = 22;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateKey(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Shared Tailwind class for standard text inputs. */
const INPUT_CLS =
  'h-11 w-full rounded-md border border-hairline-strong bg-white px-3 text-sm text-ink transition-colors placeholder:text-ink-muted focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20';

/** Shared Tailwind class for select elements. */
const SELECT_CLS =
  'h-11 w-full rounded-md border border-hairline-strong bg-white px-3 text-sm text-ink transition-colors focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20';

/** Shared Tailwind class for textarea elements. */
const TEXTAREA_CLS =
  'w-full rounded-md border border-hairline-strong bg-white px-3 py-2.5 text-sm text-ink transition-colors placeholder:text-ink-muted focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20';

// ---------------------------------------------------------------------------
// Form state types
// ---------------------------------------------------------------------------

interface Address {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface BusinessDetails {
  tradingName: string;
  legalName: string;
  mccCode: string | null;
  website: string;
  processingVolume: string;
  billingDescriptor: string;
  customerServicePhone: string;
  customerServiceEmail: string;
  deliveryTimeframe: string;
  businessDescription: string;
  registeredAddress: Address;
}

interface PrincipalDetails {
  key: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  email: string;
  ownershipPercentage: string;
  residentialAddress: Address;
  phoneNumber: string;
  idDocument: File | null;
}

interface ProcessingDetails {
  currencies: string[];
  expectedMonthlyVolume: string;
  averageTransactionSize: string;
  refundPolicy: string;
  maxTransactionSize: string;
  previousProcessorName: string;
  currentChargebackRate: string;
  expectedChargebackRate: string;
  acceptRecurring: boolean;
  acceptInternational: boolean;
}

interface ComplianceDeclarations {
  noProhibitedGoods: boolean;
  accurateInformation: boolean;
  amlCompliance: boolean;
  termsAccepted: boolean;
  consentToMonitoring: boolean;
  noPriorTermination: boolean;
  priorTerminationExplanation: string;
  pciCompliance: boolean;
  beneficialOwnershipDisclosure: boolean;
  dataProcessingConsent: boolean;
}

interface DocumentSlot {
  key: string;
  label: string;
  helperText: string;
  required: boolean;
  /** 'conditional' slots are only shown when a condition is met. */
  condition: 'always' | 'conditional' | 'recommended';
  file: File | null;
}

// ---------------------------------------------------------------------------
// Initial state factories
// ---------------------------------------------------------------------------

function createEmptyAddress(): Address {
  return { line1: '', city: '', state: '', postalCode: '', country: '' };
}

function createEmptyPrincipal(): PrincipalDetails {
  return {
    key: generateKey(),
    fullName: '',
    dateOfBirth: '',
    nationality: '',
    email: '',
    ownershipPercentage: '',
    residentialAddress: createEmptyAddress(),
    phoneNumber: '',
    idDocument: null,
  };
}

// ---------------------------------------------------------------------------
// Inline SVG icon helpers
// ---------------------------------------------------------------------------

function CheckIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PlusIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ChevronIcon({ className = 'h-4 w-4', direction = 'down' }: { className?: string; direction?: 'up' | 'down' }) {
  return (
    <svg
      className={`${className} transition-transform ${direction === 'up' ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function PencilIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Reusable sub-components
// ---------------------------------------------------------------------------

/** Label with optional required asterisk or "(Optional)" badge. */
function FieldLabel({
  htmlFor,
  required,
  optional,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('portal');
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink-muted">
      {children}
      {required && (
        <span
          className="ms-0.5 text-danger"
          aria-label={t('merchantOnboarding.requirementBadge.requiredAria')}
        >
          *
        </span>
      )}
      {optional && (
        <span className="ms-1.5 text-xs font-normal text-ink-muted">
          {t('merchantOnboarding.requirementBadge.optionalParen')}
        </span>
      )}
    </label>
  );
}

/** Small helper text beneath a field. */
function FieldHelper({ text }: { text: string }) {
  return <p className="mt-1 text-xs text-ink-muted">{text}</p>;
}

/** Subsection heading within a step card. Mono eyebrow + hairline rule. */
function SubsectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-3 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.1em] text-ink-muted">
      {children}
      <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
    </h3>
  );
}

/** Badge indicating field requirement level. */
function RequirementBadge({
  type,
}: {
  type: 'required' | 'recommended' | 'optional' | 'conditional';
}) {
  const { t } = useTranslation('portal');
  if (type === 'required') return null;
  const clsMap: Record<'recommended' | 'optional' | 'conditional', string> = {
    recommended: 'bg-primary-tint text-primary-hover',
    optional: 'bg-surface-sunken text-ink-muted',
    conditional: 'bg-warning-tint text-warning',
  };
  const labelMap: Record<'recommended' | 'optional' | 'conditional', string> = {
    recommended: t('merchantOnboarding.requirementBadge.recommended'),
    optional: t('merchantOnboarding.requirementBadge.optional'),
    conditional: t('merchantOnboarding.requirementBadge.conditional'),
  };
  return (
    <span className={`ms-2 inline-block rounded-sm px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.06em] ${clsMap[type]}`}>
      {labelMap[type]}
    </span>
  );
}

/** Address field group in compact 2-col grid. */
function AddressFields({
  address,
  onChange,
  idPrefix,
}: {
  address: Address;
  onChange: (field: keyof Address, value: string) => void;
  idPrefix: string;
}) {
  const { t } = useTranslation('portal');
  return (
    <div className="space-y-3">
      <div>
        <FieldLabel htmlFor={`${idPrefix}-line1`} required>
          {t('merchantOnboarding.fields.addressLine1')}
        </FieldLabel>
        <input
          id={`${idPrefix}-line1`}
          type="text"
          value={address.line1}
          onChange={(e) => onChange('line1', e.target.value)}
          className={INPUT_CLS}
          placeholder={t('merchantOnboarding.fields.addressLine1Placeholder')}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor={`${idPrefix}-city`} required>
            {t('merchantOnboarding.fields.city')}
          </FieldLabel>
          <input
            id={`${idPrefix}-city`}
            type="text"
            value={address.city}
            onChange={(e) => onChange('city', e.target.value)}
            className={INPUT_CLS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${idPrefix}-state`} optional>
            {t('merchantOnboarding.fields.stateProvince')}
          </FieldLabel>
          <input
            id={`${idPrefix}-state`}
            type="text"
            value={address.state}
            onChange={(e) => onChange('state', e.target.value)}
            className={INPUT_CLS}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor={`${idPrefix}-postal`} required>
            {t('merchantOnboarding.fields.postalCode')}
          </FieldLabel>
          <input
            id={`${idPrefix}-postal`}
            type="text"
            value={address.postalCode}
            onChange={(e) => onChange('postalCode', e.target.value)}
            className={INPUT_CLS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${idPrefix}-country`} required>
            {t('merchantOnboarding.fields.country')}
          </FieldLabel>
          <input
            id={`${idPrefix}-country`}
            type="text"
            value={address.country}
            onChange={(e) => onChange('country', e.target.value)}
            className={INPUT_CLS}
            placeholder={t('merchantOnboarding.fields.countryPlaceholder')}
          />
        </div>
      </div>
    </div>
  );
}

/** Format an address for display. */
function formatAddress(addr: Address): string {
  return [addr.line1, addr.city, addr.state, addr.postalCode, addr.country]
    .filter(Boolean)
    .join(', ');
}

/** Format file size for display. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MerchantOnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const merchantSteps = useMemo(() => getMerchantSteps(t), [t]);
  const deliveryTimeframes = useMemo(() => getDeliveryTimeframes(t), [t]);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted] = useState(false);

  // Principal card collapse state
  const [collapsedPrincipals, setCollapsedPrincipals] = useState<Set<string>>(new Set());

  // -- Step 1: Business --
  const [business, setBusiness] = useState<BusinessDetails>({
    tradingName: '',
    legalName: '',
    mccCode: null,
    website: '',
    processingVolume: '',
    billingDescriptor: '',
    customerServicePhone: '',
    customerServiceEmail: '',
    deliveryTimeframe: '',
    businessDescription: '',
    registeredAddress: createEmptyAddress(),
  });

  // -- Step 2: Principals --
  const [principals, setPrincipals] = useState<PrincipalDetails[]>([
    createEmptyPrincipal(),
  ]);

  // -- Step 3: Processing --
  const [processing, setProcessing] = useState<ProcessingDetails>({
    currencies: [],
    expectedMonthlyVolume: '',
    averageTransactionSize: '',
    refundPolicy: '',
    maxTransactionSize: '',
    previousProcessorName: '',
    currentChargebackRate: '',
    expectedChargebackRate: '',
    acceptRecurring: false,
    acceptInternational: false,
  });

  // -- Step 4: Compliance --
  const [compliance, setCompliance] = useState<ComplianceDeclarations>({
    noProhibitedGoods: false,
    accurateInformation: false,
    amlCompliance: false,
    termsAccepted: false,
    consentToMonitoring: false,
    noPriorTermination: false,
    priorTerminationExplanation: '',
    pciCompliance: false,
    beneficialOwnershipDisclosure: false,
    dataProcessingConsent: false,
  });

  // -- Step 5: Documents --
  const [docSlots, setDocSlots] = useState<DocumentSlot[]>(() => [
    {
      key: 'bank-verification',
      label: t('merchantOnboarding.documents.slots.bankVerification.label'),
      helperText: t('merchantOnboarding.documents.slots.bankVerification.helper'),
      required: true,
      condition: 'always',
      file: null,
    },
    {
      key: 'refund-policy',
      label: t('merchantOnboarding.documents.slots.refundPolicy.label'),
      helperText: t('merchantOnboarding.documents.slots.refundPolicy.helper'),
      required: true,
      condition: 'always',
      file: null,
    },
    {
      key: 'privacy-policy',
      label: t('merchantOnboarding.documents.slots.privacyPolicy.label'),
      helperText: t('merchantOnboarding.documents.slots.privacyPolicy.helper'),
      required: true,
      condition: 'always',
      file: null,
    },
    {
      key: 'processing-history',
      label: t('merchantOnboarding.documents.slots.processingHistory.label'),
      helperText: t('merchantOnboarding.documents.slots.processingHistory.helper'),
      required: false,
      condition: 'conditional',
      file: null,
    },
    {
      key: 'pci-certificate',
      label: t('merchantOnboarding.documents.slots.pciCertificate.label'),
      helperText: t('merchantOnboarding.documents.slots.pciCertificate.helper'),
      required: false,
      condition: 'conditional',
      file: null,
    },
    {
      key: 'website-screenshot',
      label: t('merchantOnboarding.documents.slots.websiteScreenshot.label'),
      helperText: t('merchantOnboarding.documents.slots.websiteScreenshot.helper'),
      required: false,
      condition: 'recommended',
      file: null,
    },
    {
      key: 'proof-product',
      label: t('merchantOnboarding.documents.slots.proofProduct.label'),
      helperText: t('merchantOnboarding.documents.slots.proofProduct.helper'),
      required: false,
      condition: 'recommended',
      file: null,
    },
  ]);

  // -------------------------------------------------------------------------
  // Document slot visibility
  // -------------------------------------------------------------------------

  const isDocSlotVisible = useCallback(
    (slot: DocumentSlot): boolean => {
      if (slot.condition === 'always' || slot.condition === 'recommended') {
        return true;
      }
      if (slot.key === 'processing-history') {
        return processing.previousProcessorName.trim().length > 0;
      }
      if (slot.key === 'pci-certificate') {
        return compliance.pciCompliance;
      }
      return true;
    },
    [processing.previousProcessorName, compliance.pciCompliance],
  );

  // -------------------------------------------------------------------------
  // Principal management
  // -------------------------------------------------------------------------

  const addPrincipal = useCallback(() => {
    setPrincipals((prev) => [...prev, createEmptyPrincipal()]);
  }, []);

  const removePrincipal = useCallback((key: string) => {
    setPrincipals((prev) => prev.filter((p) => p.key !== key));
    setCollapsedPrincipals((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const togglePrincipalCollapse = useCallback((key: string) => {
    setCollapsedPrincipals((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const updatePrincipal = useCallback(
    (key: string, field: keyof PrincipalDetails, value: unknown) => {
      setPrincipals((prev) =>
        prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)),
      );
    },
    [],
  );

  const updatePrincipalAddress = useCallback(
    (key: string, field: keyof Address, value: string) => {
      setPrincipals((prev) =>
        prev.map((p) =>
          p.key === key
            ? {
                ...p,
                residentialAddress: { ...p.residentialAddress, [field]: value },
              }
            : p,
        ),
      );
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Currency toggle
  // -------------------------------------------------------------------------

  const toggleCurrency = useCallback((code: string) => {
    setProcessing((prev) => {
      const exists = prev.currencies.includes(code);
      return {
        ...prev,
        currencies: exists
          ? prev.currencies.filter((c) => c !== code)
          : [...prev.currencies, code],
      };
    });
  }, []);

  // -------------------------------------------------------------------------
  // Document management
  // -------------------------------------------------------------------------

  const updateDocFile = useCallback((slotKey: string, file: File | null) => {
    setDocSlots((prev) =>
      prev.map((s) => (s.key === slotKey ? { ...s, file } : s)),
    );
  }, []);

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------

  const goNext = useCallback(() => {
    setError(null);
    setCurrentStep((prev) => Math.min(prev + 1, merchantSteps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToStep = useCallback((step: number) => {
    setError(null);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    if (!caseId) return;

    setSubmitting(true);
    setError(null);

    try {
      // Map local address to API address shape
      const toAddress = (a: Address): applicant.AddressInput => ({
        line1: a.line1,
        city: a.city,
        stateOrProvince: a.state || undefined,
        postalCode: a.postalCode,
        country: a.country,
      });

      const payload: applicant.KymApplicationInput = {
        merchant: {
          tradingName: business.tradingName,
          legalName: business.legalName,
          mccCode: business.mccCode ?? '',
          websiteUrl: business.website,
          businessDescription: business.businessDescription,
          billingDescriptor: business.billingDescriptor,
          customerServicePhone: business.customerServicePhone,
          customerServiceEmail: business.customerServiceEmail,
          deliveryTimeframe:
            (business.deliveryTimeframe as applicant.DeliveryTimeframe) ||
            undefined,
          registeredAddress: toAddress(business.registeredAddress),
        },
        principals: principals.map((p) => ({
          fullName: p.fullName,
          dateOfBirth: p.dateOfBirth,
          nationality: p.nationality,
          email: p.email,
          phoneNumber: p.phoneNumber || undefined,
          ownershipPercentage: parseFloat(p.ownershipPercentage) || 0,
          address: toAddress(p.residentialAddress),
          requiresIdUpload: p.idDocument !== null,
        })),
        processing: {
          currencies: processing.currencies,
          expectedMonthlyVolume:
            parseInt(processing.expectedMonthlyVolume, 10) || 0,
          averageTransactionSize:
            parseInt(processing.averageTransactionSize, 10) || 0,
          maximumTransactionSize: processing.maxTransactionSize
            ? parseInt(processing.maxTransactionSize, 10)
            : undefined,
          previousProcessorName:
            processing.previousProcessorName || undefined,
          currentChargebackRate: processing.currentChargebackRate
            ? parseFloat(processing.currentChargebackRate)
            : undefined,
          expectedChargebackRate: processing.expectedChargebackRate
            ? parseFloat(processing.expectedChargebackRate)
            : undefined,
          acceptsRecurringPayments: processing.acceptRecurring,
          acceptsInternationalPayments: processing.acceptInternational,
          refundPolicy: processing.refundPolicy,
        },
        compliance: {
          noProhibitedGoods: compliance.noProhibitedGoods,
          accurateInformation: compliance.accurateInformation,
          amlCompliance: compliance.amlCompliance,
          termsAccepted: compliance.termsAccepted,
          consentToMonitoring: compliance.consentToMonitoring,
          noPriorTermination: compliance.noPriorTermination,
          priorTerminationExplanation:
            compliance.priorTerminationExplanation || undefined,
          pciCompliance: compliance.pciCompliance || undefined,
          beneficialOwnershipDisclosure:
            compliance.beneficialOwnershipDisclosure,
          dataProcessingConsent: compliance.dataProcessingConsent,
        },
      };

      const client = getApiClient();
      await client.applicant.saveKymApplication(caseId, payload);

      navigate(`/case/${caseId}/confirmation`);
    } catch (err: unknown) {
      const apiErr = err as ApiClientError;
      setError(
        apiErr.message ??
          t('merchantOnboarding.errors.submitFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }, [caseId, navigate, business, principals, processing, compliance, t]);

  // -------------------------------------------------------------------------
  // Validation per step
  // -------------------------------------------------------------------------

  const isStepValid = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 0: {
          const addr = business.registeredAddress;
          return !!(
            business.legalName.trim() &&
            business.mccCode &&
            business.processingVolume.trim() &&
            business.billingDescriptor.trim() &&
            business.billingDescriptor.length <= 22 &&
            business.customerServicePhone.trim() &&
            business.customerServiceEmail.trim() &&
            business.businessDescription.trim() &&
            addr.line1.trim() &&
            addr.city.trim() &&
            addr.postalCode.trim() &&
            addr.country.trim()
          );
        }
        case 1:
          return principals.every(
            (p) =>
              p.fullName.trim() &&
              p.email.trim() &&
              p.ownershipPercentage.trim() &&
              p.residentialAddress.line1.trim() &&
              p.residentialAddress.city.trim() &&
              p.residentialAddress.country.trim() &&
              p.idDocument !== null,
          );
        case 2:
          return !!(
            processing.currencies.length > 0 &&
            processing.expectedMonthlyVolume.trim()
          );
        case 3:
          return (
            compliance.noProhibitedGoods &&
            compliance.accurateInformation &&
            compliance.amlCompliance &&
            compliance.termsAccepted &&
            compliance.consentToMonitoring &&
            (compliance.noPriorTermination ||
              compliance.priorTerminationExplanation.trim().length > 0) &&
            compliance.beneficialOwnershipDisclosure &&
            compliance.dataProcessingConsent
          );
        case 4:
          return docSlots
            .filter((s) => s.required && isDocSlotVisible(s))
            .every((s) => s.file !== null);
        case 5:
          return true;
        default:
          return false;
      }
    },
    [business, principals, processing, compliance, docSlots, isDocSlotVisible],
  );

  // -------------------------------------------------------------------------
  // Computed values
  // -------------------------------------------------------------------------

  // Compliance: count of required declarations completed
  const REQUIRED_COMPLIANCE_KEYS: (keyof ComplianceDeclarations)[] = [
    'noProhibitedGoods',
    'accurateInformation',
    'amlCompliance',
    'consentToMonitoring',
    'beneficialOwnershipDisclosure',
    'dataProcessingConsent',
    'termsAccepted',
  ];
  const completedComplianceCount = REQUIRED_COMPLIANCE_KEYS.filter(
    (k) => compliance[k] === true,
  ).length;
  // noPriorTermination is handled separately (either checked or explained)
  const noPriorTerminationSatisfied =
    compliance.noPriorTermination ||
    compliance.priorTerminationExplanation.trim().length > 0;
  const totalRequiredDeclarations = REQUIRED_COMPLIANCE_KEYS.length + 1;
  const totalCompletedDeclarations =
    completedComplianceCount + (noPriorTerminationSatisfied ? 1 : 0);

  // Documents: count of uploaded out of visible
  const visibleDocs = docSlots.filter((s) => isDocSlotVisible(s));
  const uploadedDocsCount = visibleDocs.filter((s) => s.file !== null).length;

  // Review: missing required items
  const missingRequiredDocs = docSlots.filter(
    (s) => s.required && isDocSlotVisible(s) && s.file === null,
  );
  const allRequiredStepsValid = [0, 1, 2, 3, 4].every((s) => isStepValid(s));

  // -------------------------------------------------------------------------
  // Submitted confirmation screen
  // -------------------------------------------------------------------------

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-5">
        <div className="w-full max-w-lg rounded-lg bg-white p-8 text-center border border-hairline">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-tint">
            <CheckIcon className="h-8 w-8 text-primary" />
          </div>
          <h2 className="mb-2 font-display text-3xl font-medium tracking-tight text-ink">
            {t('merchantOnboarding.review.applicationSubmittedTitle')}
          </h2>
          <p className="text-ink-muted">
            {t('merchantOnboarding.review.applicationSubmittedDescription')}
          </p>
        </div>
      </div>
    );
  }

  const stepKey = merchantSteps[currentStep].key;
  const meta = STEP_META[currentStep];

  // -------------------------------------------------------------------------
  // Summary helpers (Step 6)
  // -------------------------------------------------------------------------

  function SummarySection({
    title,
    stepIndex,
    children,
  }: {
    title: string;
    stepIndex: number;
    children: React.ReactNode;
  }) {
    return (
      <section className="rounded-lg border border-hairline bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          <button
            type="button"
            onClick={() => goToStep(stepIndex)}
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors hover:bg-surface-sunken"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            <PencilIcon />
            {t('merchantOnboarding.review.edit')}
          </button>
        </div>
        {children}
      </section>
    );
  }

  function SummaryRow({
    label,
    value,
    missing,
    colSpanFull,
  }: {
    label: string;
    value: string;
    missing?: boolean;
    colSpanFull?: boolean;
  }) {
    return (
      <>
        <dt className="text-ink-muted">{label}</dt>
        <dd
          className={`${colSpanFull ? 'sm:col-span-2' : ''} ${
            missing ? 'text-warning font-medium' : 'text-ink'
          }`}
        >
          {value || '--'}
        </dd>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 sm:px-6 py-4">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ backgroundColor: 'var(--nv-primary-color)' }}
            aria-hidden="true"
          >
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-ink">
            {t('merchantOnboarding.headerTitle')}
          </h1>
        </div>
      </header>

      {/* Step progress bar */}
      <div className="border-b border-hairline bg-white">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6">
          <StepIndicator currentStep={stepKey} steps={merchantSteps} />
        </div>
      </div>

      {/* Step counter text */}
      <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 pt-6 pb-2">
        <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
          {t('stepIndicator.stepCount', { current: currentStep + 1, total: merchantSteps.length })} &mdash; {merchantSteps[currentStep].label}
        </p>
      </div>

      {/* Main content */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 pb-6">
          {/* Step card */}
          <div className="rounded-lg bg-white p-6 md:p-8 border border-hairline">
            {/* Step header with icon */}
            <div className="mb-6 flex items-start gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: 'var(--nv-primary-color)', opacity: 0.1 }}
                aria-hidden="true"
              >
                <div style={{ color: 'var(--nv-primary-color)' }}>{meta.icon}</div>
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-ink">
                  {t([`merchantOnboarding.stepHeadings.${stepKey}.title`, ''], { defaultValue: meta.title })}
                </h2>
                <p className="mt-1 text-sm text-ink-muted">
                  {t([`merchantOnboarding.stepHeadings.${stepKey}.subtitle`, ''], { defaultValue: meta.subtitle })}
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="mb-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
              >
                {error}
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 1: Business Details                                     */}
            {/* =========================================================== */}
            {currentStep === 0 && (
              <div className="space-y-6">
                {/* -- Business Identity -- */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.businessIdentity')}</SubsectionHeading>
                  <div className="space-y-4">
                    {/* Legal Name */}
                    <div>
                      <FieldLabel htmlFor="biz-legal-name" required>{t('merchantOnboarding.fields.legalName')}</FieldLabel>
                      <input
                        id="biz-legal-name"
                        type="text"
                        value={business.legalName}
                        onChange={(e) =>
                          setBusiness({ ...business, legalName: e.target.value })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.legalNamePlaceholder')}
                      />
                    </div>

                    {/* Trading Name */}
                    <div>
                      <FieldLabel htmlFor="biz-trading-name" optional>{t('merchantOnboarding.fields.tradingName')}</FieldLabel>
                      <input
                        id="biz-trading-name"
                        type="text"
                        value={business.tradingName}
                        onChange={(e) =>
                          setBusiness({ ...business, tradingName: e.target.value })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.tradingNamePlaceholder')}
                      />
                    </div>

                    {/* MCC Selector */}
                    <div>
                      <MccSelector
                        value={business.mccCode}
                        onChange={(code) =>
                          setBusiness({ ...business, mccCode: code })
                        }
                      />
                    </div>

                    {/* Billing Descriptor */}
                    <div>
                      <FieldLabel htmlFor="biz-descriptor" required>{t('merchantOnboarding.fields.billingDescriptor')}</FieldLabel>
                      <input
                        id="biz-descriptor"
                        type="text"
                        value={business.billingDescriptor}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            billingDescriptor: e.target.value.slice(0, BILLING_DESCRIPTOR_MAX),
                          })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.billingDescriptorPlaceholder')}
                        maxLength={BILLING_DESCRIPTOR_MAX}
                      />
                      <div className="mt-1 flex items-center justify-between">
                        <FieldHelper text={t('merchantOnboarding.fields.billingDescriptorHelper')} />
                        <span
                          className={`text-xs font-medium ${
                            business.billingDescriptor.length >= 20
                              ? business.billingDescriptor.length >= BILLING_DESCRIPTOR_MAX
                                ? 'text-danger'
                                : 'text-warning'
                              : 'text-ink-muted'
                          }`}
                        >
                          {business.billingDescriptor.length}/{BILLING_DESCRIPTOR_MAX}
                        </span>
                      </div>
                    </div>

                    {/* Website */}
                    <div>
                      <FieldLabel htmlFor="biz-website" optional>{t('merchantOnboarding.fields.website')}</FieldLabel>
                      <input
                        id="biz-website"
                        type="url"
                        value={business.website}
                        onChange={(e) =>
                          setBusiness({ ...business, website: e.target.value })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.websitePlaceholder')}
                      />
                    </div>

                    {/* Business Description */}
                    <div>
                      <FieldLabel htmlFor="biz-description" required>{t('merchantOnboarding.fields.businessDescription')}</FieldLabel>
                      <textarea
                        id="biz-description"
                        value={business.businessDescription}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            businessDescription: e.target.value,
                          })
                        }
                        rows={3}
                        className={TEXTAREA_CLS}
                        placeholder={t('merchantOnboarding.fields.businessDescriptionMerchantPlaceholder')}
                      />
                      <FieldHelper text={t('merchantOnboarding.fields.businessDescriptionHelper')} />
                    </div>

                    {/* Delivery Timeframe */}
                    <div>
                      <FieldLabel htmlFor="biz-delivery">
                        {t('merchantOnboarding.fields.deliveryTimeframe')}
                        <RequirementBadge type="recommended" />
                      </FieldLabel>
                      <select
                        id="biz-delivery"
                        value={business.deliveryTimeframe}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            deliveryTimeframe: e.target.value,
                          })
                        }
                        className={SELECT_CLS}
                      >
                        {deliveryTimeframes.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* -- Customer Contact -- */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.customerContact')}</SubsectionHeading>
                  <div className="bg-surface-sunken rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <FieldLabel htmlFor="biz-cs-phone" required>{t('merchantOnboarding.fields.customerServicePhone')}</FieldLabel>
                        <input
                          id="biz-cs-phone"
                          type="tel"
                          value={business.customerServicePhone}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              customerServicePhone: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                          placeholder={t('merchantOnboarding.fields.customerServicePhonePlaceholder')}
                        />
                        <FieldHelper text={t('merchantOnboarding.fields.customerServicePhoneHelper')} />
                      </div>
                      <div>
                        <FieldLabel htmlFor="biz-cs-email" required>{t('merchantOnboarding.fields.customerServiceEmail')}</FieldLabel>
                        <input
                          id="biz-cs-email"
                          type="email"
                          value={business.customerServiceEmail}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              customerServiceEmail: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                          placeholder={t('merchantOnboarding.fields.customerServiceEmailPlaceholder')}
                        />
                      </div>
                    </div>

                    {/* Processing Volume */}
                    <div>
                      <FieldLabel htmlFor="biz-volume" required>{t('merchantOnboarding.fields.estimatedMonthlyVolumeUsd')}</FieldLabel>
                      <input
                        id="biz-volume"
                        type="text"
                        value={business.processingVolume}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            processingVolume: e.target.value,
                          })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.monthlyVolumePlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                {/* -- Business Address -- */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.businessAddress')}</SubsectionHeading>
                  <div className="bg-surface-sunken rounded-lg p-4">
                    <AddressFields
                      address={business.registeredAddress}
                      onChange={(field, value) =>
                        setBusiness({
                          ...business,
                          registeredAddress: {
                            ...business.registeredAddress,
                            [field]: value,
                          },
                        })
                      }
                      idPrefix="biz-addr"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 2: Principal Details                                    */}
            {/* =========================================================== */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-ink-muted">
                  {t('merchantOnboarding.principals.intro')}
                </p>

                <div className="space-y-4">
                  {principals.map((principal, idx) => {
                    const isCollapsed = collapsedPrincipals.has(principal.key);
                    const principalLabel = principal.fullName.trim()
                      ? principal.fullName
                      : t('merchantOnboarding.principals.label', { number: idx + 1 });
                    const hasDoc = principal.idDocument !== null;

                    return (
                      <div
                        key={principal.key}
                        className="rounded-lg border border-hairline bg-white overflow-hidden"
                      >
                        {/* Principal card header */}
                        <div className="flex items-center justify-between bg-surface-sunken px-4 py-3">
                          <button
                            type="button"
                            onClick={() => togglePrincipalCollapse(principal.key)}
                            className="flex items-center gap-2 text-start"
                            aria-expanded={!isCollapsed}
                            aria-controls={`principal-body-${principal.key}`}
                          >
                            <span className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: 'var(--nv-primary-color)' }}>
                              {idx + 1}
                            </span>
                            <span className="text-sm font-semibold text-ink">
                              {principalLabel}
                            </span>
                            {principal.ownershipPercentage && (
                              <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-ink-muted">
                                {principal.ownershipPercentage}%
                              </span>
                            )}
                            {/* Status indicators */}
                            {hasDoc && (
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-tint">
                                <CheckIcon className="h-3 w-3 text-primary" />
                              </span>
                            )}
                            <ChevronIcon
                              className="h-4 w-4 text-ink-muted"
                              direction={isCollapsed ? 'down' : 'up'}
                            />
                          </button>
                          <div className="flex items-center gap-1">
                            {principals.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePrincipal(principal.key)}
                                className="rounded p-1.5 text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger focus:outline-none focus:ring-2 focus:ring-danger/30"
                                aria-label={t('merchantOnboarding.principals.removeAria', { number: idx + 1 })}
                              >
                                <XIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Principal card body */}
                        <div
                          id={`principal-body-${principal.key}`}
                          className={`transition-all duration-200 ease-in-out ${
                            isCollapsed
                              ? 'max-h-0 overflow-hidden opacity-0'
                              : 'max-h-[2000px] opacity-100'
                          }`}
                        >
                          <div className="space-y-4 p-4">
                            {/* Full Name */}
                            <div>
                              <FieldLabel htmlFor={`principal-name-${principal.key}`} required>
                                {t('merchantOnboarding.fields.fullName')}
                              </FieldLabel>
                              <input
                                id={`principal-name-${principal.key}`}
                                type="text"
                                value={principal.fullName}
                                onChange={(e) =>
                                  updatePrincipal(principal.key, 'fullName', e.target.value)
                                }
                                className={INPUT_CLS}
                              />
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              {/* Date of Birth */}
                              <div>
                                <FieldLabel htmlFor={`principal-dob-${principal.key}`} optional>
                                  {t('merchantOnboarding.fields.dateOfBirth')}
                                </FieldLabel>
                                <input
                                  id={`principal-dob-${principal.key}`}
                                  type="date"
                                  value={principal.dateOfBirth}
                                  onChange={(e) =>
                                    updatePrincipal(principal.key, 'dateOfBirth', e.target.value)
                                  }
                                  className={INPUT_CLS}
                                />
                              </div>

                              {/* Nationality */}
                              <div>
                                <FieldLabel htmlFor={`principal-nat-${principal.key}`} optional>
                                  {t('merchantOnboarding.fields.nationality')}
                                </FieldLabel>
                                <input
                                  id={`principal-nat-${principal.key}`}
                                  type="text"
                                  value={principal.nationality}
                                  onChange={(e) =>
                                    updatePrincipal(principal.key, 'nationality', e.target.value)
                                  }
                                  className={INPUT_CLS}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              {/* Email */}
                              <div>
                                <FieldLabel htmlFor={`principal-email-${principal.key}`} required>
                                  {t('merchantOnboarding.fields.email')}
                                </FieldLabel>
                                <input
                                  id={`principal-email-${principal.key}`}
                                  type="email"
                                  value={principal.email}
                                  onChange={(e) =>
                                    updatePrincipal(principal.key, 'email', e.target.value)
                                  }
                                  className={INPUT_CLS}
                                />
                              </div>

                              {/* Phone Number */}
                              <div>
                                <FieldLabel htmlFor={`principal-phone-${principal.key}`} optional>
                                  {t('merchantOnboarding.fields.phoneNumber')}
                                </FieldLabel>
                                <input
                                  id={`principal-phone-${principal.key}`}
                                  type="tel"
                                  value={principal.phoneNumber}
                                  onChange={(e) =>
                                    updatePrincipal(principal.key, 'phoneNumber', e.target.value)
                                  }
                                  className={INPUT_CLS}
                                />
                              </div>
                            </div>

                            {/* Ownership Percentage */}
                            <div>
                              <FieldLabel htmlFor={`principal-pct-${principal.key}`} required>
                                {t('merchantOnboarding.fields.ownershipPercentage')}
                              </FieldLabel>
                              <input
                                id={`principal-pct-${principal.key}`}
                                type="text"
                                value={principal.ownershipPercentage}
                                onChange={(e) =>
                                  updatePrincipal(
                                    principal.key,
                                    'ownershipPercentage',
                                    e.target.value,
                                  )
                                }
                                className={INPUT_CLS}
                                placeholder={t('merchantOnboarding.fields.ownershipPercentagePlaceholder')}
                              />
                            </div>

                            {/* Residential Address */}
                            <div>
                              <SubsectionHeading>{t('merchantOnboarding.subsections.residentialAddress')}</SubsectionHeading>
                              <div className="bg-surface-sunken rounded-lg p-4">
                                <AddressFields
                                  address={principal.residentialAddress}
                                  onChange={(field, value) =>
                                    updatePrincipalAddress(principal.key, field, value)
                                  }
                                  idPrefix={`principal-addr-${principal.key}`}
                                />
                              </div>
                            </div>

                            {/* ID Document Upload */}
                            <div>
                              <SubsectionHeading>{t('merchantOnboarding.subsections.idDocument')}</SubsectionHeading>
                              <FileUpload
                                id={`principal-id-${principal.key}`}
                                label={t('merchantOnboarding.fields.passportOrNationalIdRequired')}
                                file={principal.idDocument}
                                onFileChange={(file) =>
                                  updatePrincipal(principal.key, 'idDocument', file)
                                }
                                accept="image/jpeg,image/png,application/pdf"
                                maxSizeBytes={20 * 1024 * 1024}
                              />
                              {principal.idDocument && (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-tint">
                                    <CheckIcon className="h-3 w-3 text-primary" />
                                  </span>
                                  <span className="text-xs text-primary-hover">{t('merchantOnboarding.principals.documentUploaded')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Principal button */}
                <button
                  type="button"
                  onClick={addPrincipal}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-hairline-strong px-4 py-3 text-sm font-medium text-ink-muted transition-colors hover:border-hairline-strong hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  <PlusIcon className="h-5 w-5" />
                  {t('merchantOnboarding.principals.addAnother')}
                </button>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 3: Processing Details                                   */}
            {/* =========================================================== */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Currency selector */}
                <div>
                  <FieldLabel required>{t('merchantOnboarding.fields.expectedCurrencies')}</FieldLabel>
                  <p className="mb-3 text-xs text-ink-muted">
                    {t('merchantOnboarding.fields.expectedCurrenciesHelper')}
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label={t('merchantOnboarding.fields.currencySelectionAria')}>
                    {CURRENCY_OPTIONS.map((code) => {
                      const selected = processing.currencies.includes(code);
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => toggleCurrency(code)}
                          className={[
                            'h-9 rounded-full border px-4 text-sm font-medium transition-all',
                            selected
                              ? 'border-[var(--nv-primary-color)] bg-[var(--nv-primary-color)] text-white'
                              : 'border-hairline-strong bg-white text-ink-muted hover:border-hairline-strong hover:bg-surface-sunken',
                          ].join(' ')}
                          aria-pressed={selected}
                        >
                          {code}
                        </button>
                      );
                    })}
                  </div>
                  {processing.currencies.length > 0 && (
                    <p className="mt-2 text-xs text-ink-muted">
                      {t('merchantOnboarding.fields.currencySelectedCount', {
                        count: processing.currencies.length,
                        list: processing.currencies.join(', '),
                      })}
                    </p>
                  )}
                </div>

                {/* Volume fields in highlighted info box */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.transactionVolumes')}</SubsectionHeading>
                  <div className="space-y-4 rounded-md border border-info/30 bg-info-tint/60 p-4">
                    <p className="text-xs text-info">
                      {t('merchantOnboarding.subsections.transactionVolumesHelper')}
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <FieldLabel htmlFor="proc-volume" required>{t('merchantOnboarding.fields.expectedMonthlyVolumeUsd')}</FieldLabel>
                        <input
                          id="proc-volume"
                          type="text"
                          value={processing.expectedMonthlyVolume}
                          onChange={(e) =>
                            setProcessing({
                              ...processing,
                              expectedMonthlyVolume: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                          placeholder={t('merchantOnboarding.fields.monthlyVolumePlaceholder')}
                        />
                      </div>
                      <div>
                        <FieldLabel htmlFor="proc-avg" optional>{t('merchantOnboarding.fields.averageTransactionSizeUsd')}</FieldLabel>
                        <input
                          id="proc-avg"
                          type="text"
                          value={processing.averageTransactionSize}
                          onChange={(e) =>
                            setProcessing({
                              ...processing,
                              averageTransactionSize: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                          placeholder={t('merchantOnboarding.fields.averageTransactionPlaceholder')}
                        />
                      </div>
                    </div>
                    <div>
                      <FieldLabel htmlFor="proc-max">
                        {t('merchantOnboarding.fields.maxTransactionSizeUsd')}
                        <RequirementBadge type="recommended" />
                      </FieldLabel>
                      <input
                        id="proc-max"
                        type="text"
                        value={processing.maxTransactionSize}
                        onChange={(e) =>
                          setProcessing({
                            ...processing,
                            maxTransactionSize: e.target.value,
                          })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.maxTransactionPlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                {/* Refund Policy */}
                <div>
                  <FieldLabel htmlFor="proc-refund" optional>{t('merchantOnboarding.fields.refundChargebackPolicy')}</FieldLabel>
                  <textarea
                    id="proc-refund"
                    value={processing.refundPolicy}
                    onChange={(e) =>
                      setProcessing({
                        ...processing,
                        refundPolicy: e.target.value,
                      })
                    }
                    rows={3}
                    className={TEXTAREA_CLS}
                    placeholder={t('merchantOnboarding.fields.refundPolicyPlaceholder')}
                  />
                </div>

                {/* Recurring / International checkboxes */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.paymentTypes')}</SubsectionHeading>
                  <div className="space-y-3 bg-surface-sunken rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={processing.acceptRecurring}
                        onChange={(e) =>
                          setProcessing({
                            ...processing,
                            acceptRecurring: e.target.checked,
                          })
                        }
                        className="h-5 w-5 rounded border-hairline-strong accent-[var(--nv-primary-color)]"
                      />
                      <div>
                        <span className="text-sm font-medium text-ink-muted">
                          {t('merchantOnboarding.paymentTypes.recurringTitle')}
                        </span>
                        <p className="text-xs text-ink-muted">{t('merchantOnboarding.paymentTypes.recurringHelper')}</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={processing.acceptInternational}
                        onChange={(e) =>
                          setProcessing({
                            ...processing,
                            acceptInternational: e.target.checked,
                          })
                        }
                        className="h-5 w-5 rounded border-hairline-strong accent-[var(--nv-primary-color)]"
                      />
                      <div>
                        <span className="text-sm font-medium text-ink-muted">
                          {t('merchantOnboarding.paymentTypes.internationalTitle')}
                        </span>
                        <p className="text-xs text-ink-muted">{t('merchantOnboarding.paymentTypes.internationalHelper')}</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Previous Processor */}
                <div>
                  <SubsectionHeading>{t('merchantOnboarding.subsections.previousProcessor')}</SubsectionHeading>
                  <div className="space-y-4">
                    <div>
                      <FieldLabel htmlFor="proc-prev" optional>{t('merchantOnboarding.fields.previousProcessorName')}</FieldLabel>
                      <input
                        id="proc-prev"
                        type="text"
                        value={processing.previousProcessorName}
                        onChange={(e) =>
                          setProcessing({
                            ...processing,
                            previousProcessorName: e.target.value,
                          })
                        }
                        className={INPUT_CLS}
                        placeholder={t('merchantOnboarding.fields.previousProcessorPlaceholder')}
                      />
                      <FieldHelper text={t('merchantOnboarding.fields.previousProcessorHelper')} />
                    </div>

                    {/* Conditional chargeback field with smooth expand */}
                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        processing.previousProcessorName.trim()
                          ? 'max-h-40 opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="rounded-lg border border-warning/20 bg-warning-tint/50 p-4">
                        <FieldLabel htmlFor="proc-cbr">
                          {t('merchantOnboarding.fields.currentChargebackRate')}
                          <RequirementBadge type="conditional" />
                        </FieldLabel>
                        <input
                          id="proc-cbr"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={processing.currentChargebackRate}
                          onChange={(e) =>
                            setProcessing({
                              ...processing,
                              currentChargebackRate: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                          placeholder={t('merchantOnboarding.fields.currentChargebackPlaceholder')}
                        />
                        <FieldHelper text={t('merchantOnboarding.fields.currentChargebackHelper')} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expected Chargeback Rate */}
                <div>
                  <FieldLabel htmlFor="proc-expected-cbr">
                    {t('merchantOnboarding.fields.expectedChargebackRate')}
                    <RequirementBadge type="recommended" />
                  </FieldLabel>
                  <input
                    id="proc-expected-cbr"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={processing.expectedChargebackRate}
                    onChange={(e) =>
                      setProcessing({
                        ...processing,
                        expectedChargebackRate: e.target.value,
                      })
                    }
                    className={INPUT_CLS}
                    placeholder={t('merchantOnboarding.fields.expectedChargebackPlaceholder')}
                  />
                </div>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 4: Compliance Declarations                              */}
            {/* =========================================================== */}
            {currentStep === 3 && (
              <div className="space-y-4">
                {/* Summary count */}
                <div className="flex items-center justify-between rounded-lg bg-surface-sunken px-4 py-3">
                  <span className="text-sm text-ink-muted">{t('merchantOnboarding.compliance.requiredCompletedLabel')}</span>
                  <span
                    className={`text-sm font-semibold ${
                      totalCompletedDeclarations >= totalRequiredDeclarations
                        ? 'text-primary'
                        : 'text-ink'
                    }`}
                  >
                    {t('merchantOnboarding.compliance.completedFraction', {
                      completed: totalCompletedDeclarations,
                      total: totalRequiredDeclarations,
                    })}
                  </span>
                </div>

                {/* Standard declarations */}
                {[
                  {
                    key: 'noProhibitedGoods' as const,
                    label: t('merchantOnboarding.compliance.declarations.noProhibitedGoods'),
                    required: true,
                  },
                  {
                    key: 'accurateInformation' as const,
                    label: t('merchantOnboarding.compliance.declarations.accurateInformation'),
                    required: true,
                  },
                  {
                    key: 'amlCompliance' as const,
                    label: t('merchantOnboarding.compliance.declarations.amlCompliance'),
                    required: true,
                  },
                  {
                    key: 'consentToMonitoring' as const,
                    label: t('merchantOnboarding.compliance.declarations.consentToMonitoring'),
                    required: true,
                  },
                  {
                    key: 'beneficialOwnershipDisclosure' as const,
                    label: t('merchantOnboarding.compliance.declarations.beneficialOwnershipDisclosure'),
                    required: true,
                  },
                  {
                    key: 'dataProcessingConsent' as const,
                    label: t('merchantOnboarding.compliance.declarations.dataProcessingConsent'),
                    required: true,
                  },
                  {
                    key: 'termsAccepted' as const,
                    label: t('merchantOnboarding.compliance.declarations.termsAccepted'),
                    required: true,
                  },
                ].map((declaration) => {
                  const checked = compliance[declaration.key];
                  return (
                    <label
                      key={declaration.key}
                      className={`flex w-full items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary/30 bg-primary-tint/40'
                          : 'border-hairline hover:border-hairline-strong'
                      } ${declaration.required ? 'border-s-4 border-s-[var(--nv-primary-color)]' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setCompliance({
                            ...compliance,
                            [declaration.key]: e.target.checked,
                          })
                        }
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-hairline-strong accent-[var(--nv-primary-color)]"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-ink-muted">
                          {declaration.label}
                        </span>
                        {declaration.required && (
                          <span className="ms-1 text-danger" aria-label={t('merchantOnboarding.requirementBadge.requiredAria')}>*</span>
                        )}
                      </div>
                      {checked && (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                          <CheckIcon className="h-3 w-3 text-primary" />
                        </span>
                      )}
                    </label>
                  );
                })}

                {/* No Prior Termination */}
                <div
                  className={`rounded-lg border p-4 transition-colors border-s-4 border-s-[var(--nv-primary-color)] ${
                    compliance.noPriorTermination || noPriorTerminationSatisfied
                      ? 'border-primary/30 bg-primary-tint/40'
                      : 'border-hairline'
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compliance.noPriorTermination}
                      onChange={(e) =>
                        setCompliance({
                          ...compliance,
                          noPriorTermination: e.target.checked,
                          priorTerminationExplanation: e.target.checked
                            ? ''
                            : compliance.priorTerminationExplanation,
                        })
                      }
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-hairline-strong accent-[var(--nv-primary-color)]"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-ink-muted">
                        {t('merchantOnboarding.compliance.declarations.noPriorTermination')}
                      </span>
                      <span className="ms-1 text-danger" aria-label={t('merchantOnboarding.requirementBadge.requiredAria')}>*</span>
                    </div>
                    {(compliance.noPriorTermination || noPriorTerminationSatisfied) && (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                        <CheckIcon className="h-3 w-3 text-primary" />
                      </span>
                    )}
                  </label>

                  {/* Expand/collapse explanation */}
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      !compliance.noPriorTermination
                        ? 'max-h-60 opacity-100 mt-3'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="ms-8">
                      <FieldLabel htmlFor="compliance-termination-explain" required>
                        {t('merchantOnboarding.compliance.explainCircumstances')}
                      </FieldLabel>
                      <textarea
                        id="compliance-termination-explain"
                        value={compliance.priorTerminationExplanation}
                        onChange={(e) =>
                          setCompliance({
                            ...compliance,
                            priorTerminationExplanation: e.target.value,
                          })
                        }
                        rows={3}
                        className={TEXTAREA_CLS}
                        placeholder={t('merchantOnboarding.compliance.explainPlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                {/* PCI Compliance */}
                <div className="rounded-lg border border-hairline p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compliance.pciCompliance}
                      onChange={(e) =>
                        setCompliance({
                          ...compliance,
                          pciCompliance: e.target.checked,
                        })
                      }
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-hairline-strong accent-[var(--nv-primary-color)]"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-ink-muted">
                        {t('merchantOnboarding.compliance.declarations.pciCompliance')}
                      </span>
                      <RequirementBadge type="conditional" />
                      <p className="mt-1 text-xs text-ink-muted">
                        {t('merchantOnboarding.compliance.declarations.pciHelper')}
                      </p>
                    </div>
                    {compliance.pciCompliance && (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                        <CheckIcon className="h-3 w-3 text-primary" />
                      </span>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 5: Document Upload                                      */}
            {/* =========================================================== */}
            {currentStep === 4 && (
              <div className="space-y-6">
                {/* Progress indicator */}
                <div className="flex items-center justify-between rounded-lg bg-surface-sunken px-4 py-3">
                  <span className="text-sm text-ink-muted">{t('merchantOnboarding.documents.uploadedLabel')}</span>
                  <span
                    className={`text-sm font-semibold ${
                      uploadedDocsCount >= visibleDocs.length ? 'text-primary' : 'text-ink'
                    }`}
                  >
                    {t('merchantOnboarding.documents.uploadedFraction', {
                      uploaded: uploadedDocsCount,
                      total: visibleDocs.length,
                    })}
                  </span>
                </div>

                {/* Required Documents */}
                {(() => {
                  const requiredDocs = docSlots.filter(
                    (s) => s.required && s.condition === 'always' && isDocSlotVisible(s),
                  );
                  if (requiredDocs.length === 0) return null;
                  return (
                    <div>
                      <div className="mb-3 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-danger" aria-hidden="true" />
                        <h3 className="text-sm font-semibold text-ink">{t('merchantOnboarding.subsections.requiredDocuments')}</h3>
                      </div>
                      <div className="space-y-4">
                        {requiredDocs.map((slot) => (
                          <div key={slot.key} className="rounded-lg border border-hairline p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <svg className="h-5 w-5 shrink-0 text-ink-muted mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                              </svg>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-ink">
                                  {slot.label} <span className="text-danger" aria-label={t('merchantOnboarding.requirementBadge.requiredAria')}>*</span>
                                </p>
                                <p className="text-xs text-ink-muted">{slot.helperText}</p>
                              </div>
                              {slot.file && (
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                                  <CheckIcon className="h-3 w-3 text-primary" />
                                </span>
                              )}
                            </div>
                            {slot.file ? (
                              <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary-tint px-4 py-3">
                                <CheckIcon className="h-5 w-5 shrink-0 text-primary" />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-ink">{slot.file.name}</p>
                                  <p className="text-xs text-ink-muted">{formatBytes(slot.file.size)}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateDocFile(slot.key, null)}
                                  className="shrink-0 rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]"
                                  aria-label={t('merchantOnboarding.documents.removeAria', { label: slot.label })}
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <FileUpload
                                id={`doc-${slot.key}`}
                                label=""
                                file={slot.file}
                                onFileChange={(f) => updateDocFile(slot.key, f)}
                                accept="image/jpeg,image/png,application/pdf"
                                maxSizeBytes={20 * 1024 * 1024}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Conditional Documents */}
                {(() => {
                  const conditionalDocs = docSlots.filter(
                    (s) => s.condition === 'conditional' && isDocSlotVisible(s),
                  );
                  if (conditionalDocs.length === 0) return null;
                  return (
                    <div>
                      <div className="mb-3 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-warning" aria-hidden="true" />
                        <h3 className="text-sm font-semibold text-ink">{t('merchantOnboarding.subsections.conditionalDocuments')}</h3>
                      </div>
                      <div className="space-y-4">
                        {conditionalDocs.map((slot) => (
                          <div key={slot.key} className="rounded-lg border border-hairline p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <svg className="h-5 w-5 shrink-0 text-ink-muted mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                              </svg>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-ink">{slot.label}</p>
                                <p className="text-xs text-ink-muted">{slot.helperText}</p>
                              </div>
                              {slot.file && (
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                                  <CheckIcon className="h-3 w-3 text-primary" />
                                </span>
                              )}
                            </div>
                            {slot.file ? (
                              <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary-tint px-4 py-3">
                                <CheckIcon className="h-5 w-5 shrink-0 text-primary" />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-ink">{slot.file.name}</p>
                                  <p className="text-xs text-ink-muted">{formatBytes(slot.file.size)}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateDocFile(slot.key, null)}
                                  className="shrink-0 rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]"
                                  aria-label={t('merchantOnboarding.documents.removeAria', { label: slot.label })}
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <FileUpload
                                id={`doc-${slot.key}`}
                                label=""
                                file={slot.file}
                                onFileChange={(f) => updateDocFile(slot.key, f)}
                                accept="image/jpeg,image/png,application/pdf"
                                maxSizeBytes={20 * 1024 * 1024}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Recommended Documents */}
                {(() => {
                  const recommendedDocs = docSlots.filter(
                    (s) => s.condition === 'recommended' && isDocSlotVisible(s),
                  );
                  if (recommendedDocs.length === 0) return null;
                  return (
                    <div>
                      <div className="mb-3 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary" aria-hidden="true" />
                        <h3 className="text-sm font-semibold text-ink">
                          {t('merchantOnboarding.subsections.recommendedDocuments')}
                          <span className="ms-2 text-xs font-normal text-ink-muted">{t('merchantOnboarding.subsections.recommendedDocumentsSuffix')}</span>
                        </h3>
                      </div>
                      <div className="space-y-4">
                        {recommendedDocs.map((slot) => (
                          <div key={slot.key} className="rounded-lg border border-hairline p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <svg className="h-5 w-5 shrink-0 text-ink-muted mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                              </svg>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-ink">{slot.label}</p>
                                <p className="text-xs text-ink-muted">{slot.helperText}</p>
                              </div>
                              {slot.file && (
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint">
                                  <CheckIcon className="h-3 w-3 text-primary" />
                                </span>
                              )}
                            </div>
                            {slot.file ? (
                              <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary-tint px-4 py-3">
                                <CheckIcon className="h-5 w-5 shrink-0 text-primary" />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-ink">{slot.file.name}</p>
                                  <p className="text-xs text-ink-muted">{formatBytes(slot.file.size)}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateDocFile(slot.key, null)}
                                  className="shrink-0 rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]"
                                  aria-label={t('merchantOnboarding.documents.removeAria', { label: slot.label })}
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <FileUpload
                                id={`doc-${slot.key}`}
                                label=""
                                file={slot.file}
                                onFileChange={(f) => updateDocFile(slot.key, f)}
                                accept="image/jpeg,image/png,application/pdf"
                                maxSizeBytes={20 * 1024 * 1024}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 6: Summary / Review                                     */}
            {/* =========================================================== */}
            {currentStep === 5 && (
              <div className="space-y-5">
                {/* Missing items warning */}
                {!allRequiredStepsValid && (
                  <div className="rounded-lg border border-warning/30 bg-warning-tint px-4 py-3 text-sm text-warning">
                    <strong>{t('merchantOnboarding.review.missingWarningStrong')}</strong>
                    {t('merchantOnboarding.review.missingWarningText')}
                  </div>
                )}

                {/* Business Details Summary */}
                <SummarySection title={t('merchantOnboarding.review.sectionTitles.businessDetails')} stepIndex={0}>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.legalName')} value={business.legalName} missing={!business.legalName.trim()} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.tradingName')} value={business.tradingName} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.mcc')} value={business.mccCode || ''} missing={!business.mccCode} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.billingDescriptor')} value={business.billingDescriptor} missing={!business.billingDescriptor.trim()} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.csPhone')} value={business.customerServicePhone} missing={!business.customerServicePhone.trim()} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.csEmail')} value={business.customerServiceEmail} missing={!business.customerServiceEmail.trim()} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.website')} value={business.website} />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.monthlyVolume')}
                      value={business.processingVolume ? `$${business.processingVolume}` : ''}
                      missing={!business.processingVolume.trim()}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.deliveryTimeframe')}
                      value={
                        deliveryTimeframes.find((opt) => opt.value === business.deliveryTimeframe)?.label || ''
                      }
                    />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.businessDescription')} value={business.businessDescription} colSpanFull missing={!business.businessDescription.trim()} />
                    <SummaryRow label={t('merchantOnboarding.review.summaryLabels.registeredAddress')} value={formatAddress(business.registeredAddress)} colSpanFull />
                  </dl>
                </SummarySection>

                {/* Principal Summary */}
                <SummarySection
                  title={t('merchantOnboarding.review.principalsTitle', { count: principals.length })}
                  stepIndex={1}
                >
                  <div className="space-y-3">
                    {principals.map((p, idx) => (
                      <div
                        key={p.key}
                        className="rounded-lg border border-hairline bg-surface-sunken p-3"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
                            style={{ backgroundColor: 'var(--nv-primary-color)' }}
                          >
                            {idx + 1}
                          </span>
                          <p className="text-sm font-medium text-ink">
                            {p.fullName || t('merchantOnboarding.principals.label', { number: idx + 1 })}
                          </p>
                        </div>
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                          <dt className="text-ink-muted">{t('merchantOnboarding.review.summaryLabels.email')}</dt>
                          <dd className="text-ink-muted">{p.email || '--'}</dd>
                          <dt className="text-ink-muted">{t('merchantOnboarding.review.summaryLabels.ownership')}</dt>
                          <dd className="text-ink-muted">
                            {p.ownershipPercentage ? `${p.ownershipPercentage}%` : '--'}
                          </dd>
                          <dt className="text-ink-muted">{t('merchantOnboarding.review.summaryLabels.address')}</dt>
                          <dd className="text-ink-muted">{formatAddress(p.residentialAddress) || '--'}</dd>
                          <dt className="text-ink-muted">{t('merchantOnboarding.review.summaryLabels.idDocument')}</dt>
                          <dd className={p.idDocument ? 'text-primary-hover' : 'text-warning font-medium'}>
                            {p.idDocument ? (
                              <span className="inline-flex items-center gap-1">
                                <CheckIcon className="h-3 w-3 text-primary" />
                                {p.idDocument.name}
                              </span>
                            ) : (
                              t('merchantOnboarding.review.notUploaded')
                            )}
                          </dd>
                        </dl>
                      </div>
                    ))}
                  </div>
                </SummarySection>

                {/* Processing Summary */}
                <SummarySection title={t('merchantOnboarding.review.sectionTitles.processingDetails')} stepIndex={2}>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.currencies')}
                      value={processing.currencies.length > 0 ? processing.currencies.join(', ') : ''}
                      missing={processing.currencies.length === 0}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.monthlyVolume')}
                      value={processing.expectedMonthlyVolume ? `$${processing.expectedMonthlyVolume}` : ''}
                      missing={!processing.expectedMonthlyVolume.trim()}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.avgTransaction')}
                      value={processing.averageTransactionSize ? `$${processing.averageTransactionSize}` : ''}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.maxTransaction')}
                      value={processing.maxTransactionSize ? `$${processing.maxTransactionSize}` : ''}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.previousProcessor')}
                      value={processing.previousProcessorName}
                    />
                    {processing.previousProcessorName.trim() && (
                      <SummaryRow
                        label={t('merchantOnboarding.review.summaryLabels.currentChargebackRate')}
                        value={processing.currentChargebackRate ? `${processing.currentChargebackRate}%` : ''}
                      />
                    )}
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.expectedChargebackRate')}
                      value={processing.expectedChargebackRate ? `${processing.expectedChargebackRate}%` : ''}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.recurring')}
                      value={processing.acceptRecurring ? t('merchantOnboarding.review.summaryLabels.yes') : t('merchantOnboarding.review.summaryLabels.no')}
                    />
                    <SummaryRow
                      label={t('merchantOnboarding.review.summaryLabels.international')}
                      value={processing.acceptInternational ? t('merchantOnboarding.review.summaryLabels.yes') : t('merchantOnboarding.review.summaryLabels.no')}
                    />
                  </dl>
                </SummarySection>

                {/* Compliance Summary */}
                <SummarySection title={t('merchantOnboarding.review.sectionTitles.complianceDeclarations')} stepIndex={3}>
                  <ul className="space-y-1.5 text-sm">
                    {[
                      { key: 'noProhibitedGoods', label: t('merchantOnboarding.compliance.summaryLabels.noProhibitedGoods') },
                      { key: 'accurateInformation', label: t('merchantOnboarding.compliance.summaryLabels.accurateInformation') },
                      { key: 'amlCompliance', label: t('merchantOnboarding.compliance.summaryLabels.amlCompliance') },
                      { key: 'consentToMonitoring', label: t('merchantOnboarding.compliance.summaryLabels.consentToMonitoring') },
                      { key: 'beneficialOwnershipDisclosure', label: t('merchantOnboarding.compliance.summaryLabels.beneficialOwnershipDisclosure') },
                      { key: 'dataProcessingConsent', label: t('merchantOnboarding.compliance.summaryLabels.dataProcessingConsent') },
                      { key: 'termsAccepted', label: t('merchantOnboarding.compliance.summaryLabels.termsAccepted') },
                      { key: 'noPriorTermination', label: t('merchantOnboarding.compliance.summaryLabels.noPriorTermination') },
                      { key: 'pciCompliance', label: t('merchantOnboarding.compliance.summaryLabels.pciCompliance') },
                    ].map((item) => {
                      const checked =
                        compliance[item.key as keyof ComplianceDeclarations];
                      return (
                        <li key={item.key} className="flex items-center gap-2">
                          {checked ? (
                            <CheckIcon className="h-4 w-4 text-primary" />
                          ) : (
                            <XIcon className="h-4 w-4 text-ink-muted" />
                          )}
                          <span className={checked ? 'text-ink' : 'text-ink-muted'}>
                            {item.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {!compliance.noPriorTermination &&
                    compliance.priorTerminationExplanation.trim() && (
                      <div className="mt-3 rounded border border-warning/30 bg-warning-tint p-3 text-sm text-warning">
                        <strong>{t('merchantOnboarding.compliance.priorTerminationLabel')}</strong>{' '}
                        {compliance.priorTerminationExplanation}
                      </div>
                    )}
                </SummarySection>

                {/* Documents Summary */}
                <SummarySection title={t('merchantOnboarding.review.sectionTitles.documents')} stepIndex={4}>
                  <div className="mb-3 flex items-center justify-between text-xs">
                    <span className="text-ink-muted">{t('merchantOnboarding.documents.uploadProgress')}</span>
                    <span className="font-medium text-ink-muted">
                      {t('merchantOnboarding.documents.uploadedOfTotal', {
                        uploaded: uploadedDocsCount,
                        total: visibleDocs.length,
                      })}
                    </span>
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {docSlots
                      .filter((s) => isDocSlotVisible(s))
                      .map((s) => {
                        const isMissing = s.required && !s.file;
                        return (
                          <li
                            key={s.key}
                            className={`flex items-center gap-2 ${
                              isMissing ? 'text-warning' : s.file ? 'text-primary-hover' : 'text-ink-muted'
                            }`}
                          >
                            {s.file ? (
                              <CheckIcon className="h-4 w-4 shrink-0 text-primary" />
                            ) : (
                              <XIcon className="h-4 w-4 shrink-0 text-ink-muted" />
                            )}
                            <span>
                              {s.label}
                              {s.file && (
                                <span className="ms-1.5 text-xs text-ink-muted">
                                  ({s.file.name})
                                </span>
                              )}
                              {isMissing && (
                                <span className="ms-1.5 text-xs font-medium text-warning">
                                  {t('merchantOnboarding.review.required')}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                </SummarySection>

                {/* Terms reminder & submit */}
                <div className="mt-6 space-y-4">
                  {missingRequiredDocs.length > 0 && (
                    <div className="rounded-lg border border-warning/30 bg-warning-tint px-4 py-3 text-sm text-warning">
                      <strong>
                        {missingRequiredDocs.length > 1
                          ? t('merchantOnboarding.review.missingDocsLabelPlural', { count: missingRequiredDocs.length })
                          : t('merchantOnboarding.review.missingDocsLabel', { count: missingRequiredDocs.length })}
                      </strong>{' '}
                      {missingRequiredDocs.map((d) => d.label).join(', ')}
                    </div>
                  )}
                  <p className="text-center text-xs text-ink-muted">
                    {t('merchantOnboarding.review.submitDisclaimer')}
                  </p>
                </div>
              </div>
            )}

            {/* =========================================================== */}
            {/* Navigation buttons                                           */}
            {/* =========================================================== */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
              {currentStep > 0 ? (
                <button
                  type="button"
                  onClick={goBack}
                  className="h-11 rounded-lg border border-hairline-strong px-6 text-sm font-medium text-ink-muted hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors order-2 sm:order-1"
                  style={{
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {t('merchantOnboarding.actions.back')}
                </button>
              ) : (
                <div className="order-2 sm:order-1" />
              )}

              {currentStep < merchantSteps.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!isStepValid(currentStep)}
                  className="h-11 rounded-md px-6 text-sm font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50 order-1 sm:order-2"
                  style={{
                    backgroundColor: 'var(--nv-primary-color)',
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {t('merchantOnboarding.actions.next')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!allRequiredStepsValid || submitting}
                  className="h-12 inline-flex items-center justify-center gap-2 rounded-md px-8 text-base font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50 order-1 sm:order-2"
                  style={{
                    backgroundColor: 'var(--nv-primary-color)',
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {submitting && (
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  )}
                  {submitting ? t('merchantOnboarding.actions.submitting') : t('merchantOnboarding.actions.submit')}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-hairline bg-white py-4">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center text-xs text-ink-muted">
          <p>
            {t('merchantOnboarding.footer.needHelpPrefix')}
            <a
              href="mailto:support@noxverify.com"
              className="font-medium underline hover:text-ink-muted"
              style={{ color: 'var(--nv-primary-color)' }}
            >
              support@noxverify.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
